import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  IconButton,
  Divider,
  Paper,
  Breadcrumbs,
  Link,
  Chip,
  Alert
} from '@mui/material';
import {
  DeleteOutline,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCartOutlined,
  Bolt as BoltIcon,
  VerifiedUserOutlined as VerifiedIcon,
  ArrowForward as ArrowForwardIcon,
  ShoppingBagOutlined
} from '@mui/icons-material';
import { useCart } from '../../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { getProductImage } from '../../../utils/imageHelper';

const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, subtotal, totalItems } = useCart();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: 'rgba(14, 106, 218, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <ShoppingCartOutlined sx={{ fontSize: 50, color: '#0E6ADA' }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, color: '#0F172A' }}>
          Your Cart is Empty
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 460, mx: 'auto' }}>
          Looks like you haven't added any software licenses yet. Explore genuine Windows, Office, Adobe, and Server licenses.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/shop')}
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 1.5,
            px: 4,
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#0E6ADA',
            '&:hover': { bgcolor: '#0c57b3' }
          }}
        >
          Explore Software Products
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link underline="hover" color="inherit" href="/" sx={{ fontSize: '0.9rem' }}>Home</Link>
        <Typography color="text.primary" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Shopping Cart</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
          Shopping Cart ({totalItems} item{totalItems > 1 ? 's' : ''})
        </Typography>
        <Button
          variant="text"
          onClick={() => navigate('/shop')}
          sx={{ fontWeight: 600, color: '#0E6ADA', textTransform: 'none' }}
        >
          ← Continue Shopping
        </Button>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFF' }}>
            {/* Header row on desktop */}
            <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: '3fr 1fr 1.2fr 1fr', pb: 2, borderBottom: '1px solid #E2E8F0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', letterSpacing: 0.5 }}>PRODUCT</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', letterSpacing: 0.5 }}>PRICE</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', letterSpacing: 0.5 }}>QUANTITY</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', letterSpacing: 0.5, textAlign: 'right' }}>TOTAL</Typography>
            </Box>

            {cartItems.map((item, index) => (
              <Box
                key={`${item.id}-${item.size}`}
                sx={{
                  py: 3,
                  borderBottom: index < cartItems.length - 1 ? '1px solid #F1F5F9' : 'none',
                  display: { xs: 'block', sm: 'grid' },
                  gridTemplateColumns: { sm: '3fr 1fr 1.2fr 1fr' },
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {/* Product Column */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: { xs: 2, sm: 0 } }}>
                  <Box
                    component="img"
                    src={getProductImage(item.image || item.featuredImage)}
                    alt={item.name}
                    sx={{
                      width: 72,
                      height: 72,
                      backgroundColor: '#F8FAFC',
                      borderRadius: 2,
                      border: '1px solid #E2E8F0',
                      objectFit: 'contain',
                      p: 0.8
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
                      Edition / Type: <span style={{ fontWeight: 600, color: '#334155' }}>{item.size || 'Standard License'}</span>
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                      onClick={() => removeFromCart(item.id, item.size)}
                      sx={{
                        mt: 0.8,
                        p: 0,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>

                {/* Price Column */}
                <Box sx={{ display: { xs: 'flex', sm: 'block' }, justifyContent: 'space-between', mb: { xs: 1, sm: 0 } }}>
                  <Typography variant="caption" sx={{ display: { sm: 'none' }, color: '#64748B', fontWeight: 600 }}>Price:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
                    ₹{item.price}
                  </Typography>
                </Box>

                {/* Quantity Controls */}
                <Box sx={{ display: { xs: 'flex', sm: 'block' }, justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
                  <Typography variant="caption" sx={{ display: { sm: 'none' }, color: '#64748B', fontWeight: 600 }}>Quantity:</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #CBD5E1', borderRadius: 1.5, bgcolor: '#FFF' }}>
                    <IconButton size="small" onClick={() => updateQuantity(item.id, item.size, -1)} sx={{ p: 0.6 }}>
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography sx={{ px: 1.8, fontWeight: 700, fontSize: '0.9rem' }}>{item.quantity}</Typography>
                    <IconButton size="small" onClick={() => updateQuantity(item.id, item.size, 1)} sx={{ p: 0.6 }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Total Column */}
                <Box sx={{ display: { xs: 'flex', sm: 'block' }, justifyContent: 'space-between', textAlign: { sm: 'right' } }}>
                  <Typography variant="caption" sx={{ display: { sm: 'none' }, color: '#64748B', fontWeight: 600 }}>Item Total:</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0E6ADA' }}>
                    ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>

          {/* Delivery & Guarantee Banner */}
          <Paper elevation={0} sx={{ p: 2.5, mt: 3, borderRadius: 3, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 2 }}>
            <BoltIcon sx={{ color: '#0E6ADA', fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                Instant Digital Delivery
              </Typography>
              <Typography variant="caption" sx={{ color: '#3B82F6' }}>
                Official license keys and setup instructions are sent instantly to your email & WhatsApp right after payment.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3.5, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC', position: 'sticky', top: 90 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 800, color: '#0F172A' }}>
              Order Summary
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalItems}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>₹{subtotal.toLocaleString('en-IN')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Digital Delivery</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#10B981' }}>FREE (Instant)</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Grand Total</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0E6ADA' }}>
                ₹{subtotal.toLocaleString('en-IN')}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/checkout')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.8,
                fontWeight: 700,
                fontSize: '0.95rem',
                borderRadius: 2,
                bgcolor: '#0E6ADA',
                boxShadow: '0 4px 14px rgba(14, 106, 218, 0.3)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#0c57b3',
                  boxShadow: '0 6px 18px rgba(14, 106, 218, 0.4)'
                }
              }}
            >
              Proceed to Checkout
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2.5, color: '#64748B' }}>
              <VerifiedIcon sx={{ fontSize: 18, color: '#10B981' }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                100% Genuine Licenses • Instant Delivery
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage;
